import { useCallback } from "react"
import {
  interpolate,
  interpolateParts,
  type TextArguments,
  type TextInterpolationPart,
  type TextInterpolationPartsOptions,
} from "@/v2-common/textInterpolation"
import { FALLBACK_LANGUAGE_TAG, getLocale, useLocale } from "@/v2-text/locale"
import type {
  OfflineText,
  OfflineTextCatalog,
  OfflineTextValue,
} from "@/v2-text/offline/types"

/**
 * Builds a locale-aware reader over a bundled multi-language catalog. Every
 * consumer of offline text — v2-ui primitives, v2-console app chrome
 * (loading, error boundary, auth screens, toasts fired outside the DB
 * provider) — hangs off this factory so all offline strings resolve against
 * the same `locale.ts` module store and share fallback semantics (missing
 * locale → `en-GB`).
 *
 * The returned reader is typed to the `en-GB` entry of each key: at runtime
 * `getText` still returns the caller's locale (with `en-GB` as fallback),
 * but the STATIC type stays the `en-GB` literal so `interpolate(...)` can
 * infer placeholder types from the source-language template. This assumes
 * every translation preserves the same `{placeholder}` names as `en-GB`,
 * which is enforced by the translation review flow rather than the type
 * system — but it's the same assumption `interpolate` already carries.
 *
 * Two entry points:
 *
 * - `useOfflineText()` for React components; subscribes to locale changes.
 *   Returns `{ getText, interpolate, interpolateParts }` — the same call
 *   shapes as the DB text context, so a component reads identically whether
 *   its text is bundled or fetched.
 * - `getTextSync(key)` / `interpolateSync(key, args)` for non-React callers
 *   (utility functions, `onError` callbacks that compose text at error
 *   time). Reads the module locale once at call time; no subscription.
 *
 * Unlike the context's interpolate (which erases key types to `TextsBase`),
 * these are fully typed: the `en-GB` literal flows into `TextArguments`, so
 * placeholder names and types are checked at the call site.
 */
export function makeOfflineText<C extends OfflineTextCatalog>(catalog: C) {
  type Key = Extract<keyof C, string>
  // Type-only view of the en-GB entry — preserves literal `Name` (and
  // optional `Description`) so callers can pipe it into `interpolate`.
  // The runtime value may come from any locale; the shape is identical.
  type TypedValue<K extends Key> = C[K] extends { "en-GB": infer V }
    ? V
    : OfflineTextValue

  function resolve<K extends Key>(key: K, locale: string): TypedValue<K> {
    const entry: OfflineText = catalog[key]
    return (entry[locale] ?? entry[FALLBACK_LANGUAGE_TAG]) as TypedValue<K>
  }

  // The runtime Name may come from any locale, but the static type is the
  // en-GB literal (see TypedValue) — placeholder inference follows it.
  type Name<K extends Key> =
    TypedValue<K> extends { Name: infer N extends string } ? N : string

  // TypedValue<K> is conditional, so TS can't see `.Name` on it inside the
  // generic body — go through the runtime shape and re-assert the literal.
  function resolveName<K extends Key>(key: K, locale: string): Name<K> {
    return (resolve(key, locale) as OfflineTextValue).Name as Name<K>
  }

  function getTextSync<K extends Key>(key: K): TypedValue<K> {
    return resolve(key, getLocale())
  }

  function interpolateSync<K extends Key>(
    key: K,
    args: TextArguments<Name<K>>,
  ): string {
    return interpolate(resolveName(key, getLocale()), args)
  }

  function useOfflineText() {
    const locale = useLocale()
    const getText = useCallback(
      <K extends Key>(key: K): TypedValue<K> => resolve(key, locale),
      [locale],
    )
    const interpolateText = useCallback(
      <K extends Key>(key: K, args: TextArguments<Name<K>>): string =>
        interpolate(resolveName(key, locale), args),
      [locale],
    )
    const interpolateTextParts = useCallback(
      <K extends Key>(
        key: K,
        args: TextArguments<Name<K>>,
        options?: TextInterpolationPartsOptions,
      ): TextInterpolationPart[] =>
        interpolateParts(resolveName(key, locale), args, options),
      [locale],
    )
    return {
      getText,
      interpolate: interpolateText,
      interpolateParts: interpolateTextParts,
    }
  }

  return { useOfflineText, getTextSync, interpolateSync }
}
